import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from 'react-router-dom';
import AIHumanizer from './components/AIHumanizer';
import LandingPage from './components/LandingPage';
import LoginPage from './components/auth/LoginPage';
import RegisterPage from './components/auth/RegisterPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import PaymentPage from './components/payment/PaymentPage';
import ProfilePage from './components/profile/ProfilePage';
import AdminPage from './components/admin/AdminPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import TermsConditionsPage from './components/TermsConditionsPage';
import AppLayout from './components/layout/AppLayout';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CreditsProvider } from './contexts/CreditsContext';
import { ThemeProvider } from './contexts/ThemeContext';
import './App.css';

// Protected route component to redirect unauthenticated users to login
function ProtectedRoute({ children }) {
  const { currentUser } = useAuth();
  
  // Allow both authenticated and anonymous users through
  // Individual components will handle anonymous user restrictions
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  return children;
}

// Admin route component that checks for both authentication and admin status
function AdminRoute({ children }) {
  const { currentUser, isAdmin } = useAuth();
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  if (!isAdmin) {
    return <Navigate to="/profile" />;
  }
  
  return children;
}

// Separate component for routes to avoid circular dependencies
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsConditionsPage />} />
      
      {/* Protected routes with AppLayout */}
      <Route element={
        <ProtectedRoute>
          <AppLayout />
        </ProtectedRoute>
      }>
        <Route path="/app" element={<AIHumanizer />} />
        <Route path="/payment" element={<PaymentPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/admin" element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        } />
      </Route>
    </Routes>
  );
};

function AppContent() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppRoutes />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <CreditsProvider>
        <ThemeProvider>
          <Router>
            <AppContent />
          </Router>
        </ThemeProvider>
      </CreditsProvider>
    </AuthProvider>
  );
}

export default App;
